import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Monitor, Smartphone, Loader2, X } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../services/api';

/* Global test-case search: type a name, get matching web + mobile test cases in
   a dropdown; pick one to jump to its full cross-run history page. */

const PRODUCT_THEME = {
  'New Gear': 'bg-blue-50 text-blue-700',
  'Used Gear': 'bg-purple-50 text-purple-700',
  Connect: 'bg-emerald-50 text-emerald-700',
};

function historyHref(r) {
  const p = new URLSearchParams({ source: r.source, title: r.title });
  if (r.source === 'web' && r.file_path) p.set('file_path', r.file_path);
  if (r.source === 'mobile' && r.suite) p.set('suite', r.suite);
  return `/test-case?${p.toString()}`;
}

const TestCaseSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const debounce = useRef(null);

  const runSearch = useCallback(async (term) => {
    if (!term.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.searchTestCases(term.trim());
      setResults(res.data?.results || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(q), 220);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q, runSearch]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (r) => {
    setOpen(false);
    setQ('');
    setResults([]);
    navigate(historyHref(r));
  };

  const onKeyDown = (e) => {
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-xs">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search test cases…"
          className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
        />
        {q && (
          <button
            onClick={() => {
              setQ('');
              setResults([]);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div className="absolute z-40 mt-1 w-[22rem] max-w-[calc(100vw-2rem)] right-0 sm:left-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500">No matching test cases.</div>
          )}
          {!loading && results.length > 0 && (
            <ul className="max-h-96 overflow-y-auto py-1">
              {results.map((r, i) => (
                <li key={`${r.source}:${r.suite}:${r.title}:${i}`}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                    className={clsx(
                      'w-full text-left px-3 py-2 flex items-start gap-2.5',
                      active === i ? 'bg-primary-50' : 'hover:bg-gray-50'
                    )}
                  >
                    <span className="mt-0.5 shrink-0">
                      {r.source === 'mobile' ? (
                        <Smartphone className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Monitor className="h-4 w-4 text-gray-400" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-gray-800 truncate" title={r.title}>
                        {r.title}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span
                          className={clsx(
                            'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium',
                            PRODUCT_THEME[r.product] || 'bg-gray-100 text-gray-600'
                          )}
                        >
                          {r.product}
                        </span>
                        {r.journey && (
                          <span className="text-[11px] text-gray-400">{r.journey}</span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TestCaseSearch;
